import { Card } from "@solomon-apps/shared/src/components/ui/card";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import { motion } from "motion/react";
import type { ReactElement } from "react";

import type {
    AgreementEnvelopeView,
    AgreementType
} from "@/services/clientOnboarding";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";

/**
 * The agreement panels track one envelope; they never start one.
 *
 * Agreements are sent by the issuer's approval, and resent by the issuer
 * after a decline, a void, or a bounced address. The client's part is to
 * sign from the DocuSign email, and to correct the Authorized Signatory
 * section if the email went to the wrong person. Everything rendered here
 * comes off the application bundle, so this panel and the hub's other
 * surfaces can never describe different envelope state.
 */

interface EnvelopeCopy {
    title: string;
    body: string;
    tone: "neutral" | "success" | "error";
}

function formatMoment(value: string | null): string | null {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

/** What the client should read, and do, at each point in the lifecycle. */
export function agreementCopy(
    envelope: AgreementEnvelopeView | null,
    signatoryEmail: string | null,
    agreementTitle: string,
    applicationApproved: boolean
): EnvelopeCopy {
    const recipient = signatoryEmail
        ? `your authorized signatory at ${signatoryEmail}`
        : "your authorized signatory";
    const resendLine = `Solomon will send a new one to ${recipient}. If that address is wrong, update the Authorized Signatory section first.`;

    if (!envelope) {
        return applicationApproved
            ? {
                  title: "Being prepared",
                  body: `Your application is approved. The ${agreementTitle} is being prepared and will reach ${recipient} shortly.`,
                  tone: "neutral"
              }
            : {
                  title: "Waiting on review",
                  body: `Solomon sends the ${agreementTitle} to ${recipient} once your application is approved. Nothing to do here yet.`,
                  tone: "neutral"
              };
    }

    switch (envelope.status) {
        case "created":
            return {
                title: "Being prepared",
                body: `The ${agreementTitle} is prepared and the signing email is on its way to ${recipient}.`,
                tone: "neutral"
            };
        case "sent": {
            const sentAt = formatMoment(envelope.sentAt);
            return {
                title: "Sent for signature",
                body: signatoryEmail
                    ? `Sent to ${signatoryEmail}${sentAt ? ` on ${sentAt}` : ""}. Check that inbox to sign.`
                    : `Sent to your authorized signatory${sentAt ? ` on ${sentAt}` : ""}. Check that inbox to sign.`,
                tone: "neutral"
            };
        }
        case "delivered":
            return {
                title: "Opened by your signatory",
                body: "Your signatory opened the email. They can sign whenever they are ready.",
                tone: "neutral"
            };
        case "client_signed":
            return {
                title: "Signed, awaiting counter-signature",
                body: "You have signed. The counter-signature finalizes the agreement, and your console opens once every agreement is fully signed.",
                tone: "neutral"
            };
        case "signed":
            return {
                title: "Fully signed",
                body: "Both parties have signed. This agreement is on file.",
                tone: "success"
            };
        case "declined":
            return {
                title: "Declined",
                body: `${envelope.declinedReason ?? "The signing request was declined."} ${resendLine}`,
                tone: "error"
            };
        case "voided":
            return {
                title: "Withdrawn",
                body: `This agreement was withdrawn before it was signed. ${resendLine}`,
                tone: "error"
            };
        case "email_bounced":
            return {
                title: "Signing email could not be delivered",
                body: `The signing email did not reach ${recipient}. Update the Authorized Signatory section with a working address, and Solomon will send a new one.`,
                tone: "error"
            };
    }
}

const TONE_CLASS: Record<EnvelopeCopy["tone"], string> = {
    neutral: "text-foreground-muted",
    success: "text-success-base",
    error: "text-error-base"
};

export function AgreementSigningPanel({
    agreementType,
    agreementTitle
}: {
    agreementType: AgreementType;
    agreementTitle: string;
}): ReactElement {
    const context = useOnboardingApplicationContextOrNull();
    const signatoryEmail = context?.application.signatoryEmail ?? null;
    const applicationStatus = context?.application.status;
    const applicationApproved =
        applicationStatus === "awaiting_agreements" ||
        applicationStatus === "approved";
    const envelope =
        context?.agreements.find(
            (entry) => entry.agreementType === agreementType
        ) ?? null;
    const copy = agreementCopy(
        envelope,
        signatoryEmail,
        agreementTitle,
        applicationApproved
    );

    const timeline: { label: string; at: string }[] = envelope
        ? [
              { label: "Sent", at: formatMoment(envelope.sentAt) },
              { label: "Opened", at: formatMoment(envelope.deliveredAt) },
              {
                  label: "Signed by you",
                  at: formatMoment(envelope.clientSignedAt)
              },
              {
                  label: "Counter-signed",
                  at: formatMoment(envelope.issuerSignedAt)
              }
          ].flatMap((entry) =>
              entry.at ? [{ label: entry.label, at: entry.at }] : []
          )
        : [];

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title={agreementTitle}
                className="pb-4 border-b border-line-base"
            />

            <Text
                variant="small"
                className="block leading-relaxed pt-4 text-foreground-muted"
            >
                Solomon sends this agreement for e-signature via DocuSign once
                your application is approved. Your authorized signatory signs
                from the email; the counter-signature completes it.
            </Text>

            <Card border={false} className="px-5 py-5 mt-8">
                <Heading
                    title={copy.title}
                    titleVariant="h3"
                    className="pb-2"
                />
                <Text
                    variant="small"
                    className={`block leading-relaxed ${TONE_CLASS[copy.tone]}`}
                >
                    {copy.body}
                </Text>
                {timeline.length > 0 && (
                    <ul className="pt-3 space-y-1">
                        {timeline.map((entry) => (
                            <li key={entry.label}>
                                <Text
                                    variant="label"
                                    className="block leading-snug text-foreground-muted"
                                >
                                    {entry.label} {entry.at}
                                </Text>
                            </li>
                        ))}
                    </ul>
                )}
            </Card>
        </motion.div>
    );
}

// `displayNumber` is part of the onboarding page's panel contract; the
// signing panels render their own heading and do not show it.
export function MsaSigningPanel(_props: {
    displayNumber: string | null;
}): ReactElement {
    return (
        <AgreementSigningPanel
            agreementType="msa"
            agreementTitle="Master Services Agreement"
        />
    );
}
